import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const button = cva(
  [
    'group/btn inline-flex select-none items-center justify-center gap-2 rounded-pill',
    'font-display font-semibold whitespace-nowrap',
    'transition-[background-color,color,border-color,transform,box-shadow]',
    'duration-200 ease-bounce',
    'focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ink',
    'hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.97]',
    'disabled:pointer-events-none disabled:opacity-45 disabled:hover:translate-y-0',
  ],
  {
    variants: {
      variant: {
        // Default: solid near-black pill, light text (inverts in dark mode).
        solid: 'bg-solid text-on-solid hover:bg-solid-hover',
        // High-emphasis CTA: coral fill, near-white text, soft colored lift.
        accent: 'bg-accent text-on-accent shadow-pop hover:bg-accent-hover',
        // Loudest CTA: juicy flavor gradient with a hard sticker shadow.
        candy:
          'border-2 border-ink text-ink shadow-hard [background-image:linear-gradient(100deg,var(--color-flavor-citron),var(--color-flavor-peach)_45%,var(--color-flavor-lychee))] hover:shadow-[7px_7px_0_0_var(--color-ink)]',
        // Quiet: hairline outline that firms up on hover.
        outline: 'border border-hairline-strong text-ink hover:border-ink hover:bg-surface-sunken',
        // Inline text action with an animated underline.
        link: 'text-ink underline decoration-hairline-strong decoration-1 underline-offset-4 hover:decoration-ink',
      },
      size: {
        sm: 'h-9 px-4 text-[0.8125rem]',
        md: 'h-11 px-5 text-sm',
        lg: 'h-[3.25rem] px-7 text-[0.9375rem]',
        xl: 'h-14 px-9 text-base',
      },
    },
    compoundVariants: [{ variant: 'link', class: 'h-auto rounded-none px-0 hover:translate-y-0' }],
    defaultVariants: { variant: 'solid', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp ref={ref} className={cn(button({ variant, size }), className)} {...props} />;
  },
);
Button.displayName = 'Button';
